/**
 * The document surface — the Panel, the DeCA screens and the import — behind one
 * guard.
 *
 * A pathless layout route rather than a check inside each of the six screens: it is
 * one statement instead of six, and the route tree then says which screens belong to
 * the surface. Hiding the nav entries alone would not do — a bookmark on `/decas`
 * would still render the empty list that issue #14 is about.
 *
 * The redirect is `replace`, so Back does not bounce a super-admin between a screen
 * that is not theirs and the one it sent them to.
 *
 * A null principal renders nothing rather than asking `landingPathFor`: that function
 * falls back to `/`, which is itself inside this guarded surface (Task 2's own test
 * documents the fallback as deliberate, for the render that happens while a session is
 * still resolving). Today `auth.tsx` always sets the principal before flipping `status` to
 * `'authenticated'`, in both `signIn` and `refresh`, so this route never actually mounts
 * with a null principal — but relying on that elsewhere staying true is exactly the kind of
 * coupling that turns into a silent blank portal the next time `auth.tsx` changes.
 */

import { Navigate, Outlet } from '@tanstack/react-router'

import { useAuth } from '../app/auth.tsx'
import { landingPathFor } from '../deca/navigation.ts'
import { abilitiesFor } from '../deca/roles.ts'

export function DecaSurface() {
  const { principal } = useAuth()
  if (!principal) return null
  if (!abilitiesFor(principal).handlesDecas) {
    return <Navigate to={landingPathFor(principal)} replace />
  }
  return <Outlet />
}
